import type { PropsWithChildren, FunctionComponent } from 'react'
import {
  CaptchaCheckbox,
  type CaptchaCheckboxProps,
  spacing,
  FormFieldError,
} from '@retailer-platform/shared-components'
import { Controller, type Control } from 'react-hook-form-7'
import { Controller as LegacyController, type Control as LegacyControl } from 'react-hook-form-5'
import { useDashMessage } from '../../../intl/intl.hooks'
import { useCaptchaProps } from '../../utils/captcha/useCaptchaProps.hooks'

export type FormCaptchaCheckboxProps = Omit<
  CaptchaCheckboxProps,
  'sitekey' | 'onChange' | 'provider' | 'onLoad'
> & {
  errors: Record<string, unknown>
  required?: boolean
} & (
    | {
        useLegacyRHF: false
        control: Control<any>
      }
    | {
        useLegacyRHF: true
        control: LegacyControl<any>
      }
  )

/**
 * This is a wrapper around the CaptchaCheckbox component that adds the required environment variables, and
 * accounts for either legacy or new react-hook-form versions.
 *
 * This makes it easier to use on forms.
 *
 * At some point, we should probably remove the component above and leave only this one.
 *
 * @param props
 * @returns
 */
export const FormCaptchaCheckbox: FunctionComponent<
  PropsWithChildren<FormCaptchaCheckboxProps>
> = props => {
  const errorMessage = useDashMessage('components.ippCaptchaCheckbox.error')
  const { sitekey, provider, fieldName } = useCaptchaProps()
  const isRequired = props.required ?? true

  return (
    <div css={{ padding: spacing.X8 }}>
      {props.useLegacyRHF === true ? (
        <LegacyController
          name={fieldName}
          rules={{ required: isRequired }}
          as={CaptchaCheckbox}
          control={props.control}
          sitekey={sitekey}
          action={props.action}
          className={props.className}
          provider={provider}
          data-testid={props['data-testid']}
        />
      ) : (
        <Controller
          control={props.control}
          shouldUnregister
          name={fieldName}
          rules={{ required: isRequired }}
          render={({ field: { onChange } }) => (
            <CaptchaCheckbox
              onChange={onChange}
              provider={provider}
              sitekey={sitekey}
              action={props.action}
              className={props.className}
              data-testid={props['data-testid']}
            />
          )}
        />
      )}

      {props.errors?.[fieldName] && (
        <FormFieldError data-testid={`${props['data-testid']}-error`}>
          {errorMessage}
        </FormFieldError>
      )}
    </div>
  )
}
